import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { AdminService } from '../../services/admin';
import { Partner } from '../../../../shared/models/partner';
import { PartnerFormData } from '../../../../shared/models/partner-form-data';
import { PartnerForm } from '../partner-form/partner-form';
import { PermissionService } from '../../../../core/authorization/permission';
import { Observable, of } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

@Component({
	selector: 'app-partners',
	standalone: true,
	imports: [CommonModule, MatTableModule, MatButtonModule, MatIconModule, MatSortModule, MatPaginatorModule, MatFormFieldModule, MatInputModule, FormsModule],
	templateUrl: './partners.html',
	styleUrl: './partners.css'
})
export class Partners implements OnInit, AfterViewInit {
  	dataSource       : MatTableDataSource<Partner> = new MatTableDataSource<Partner>([]);
    displayedColumns : string[]     = ['displayName', 'email', 'disabled', 'paysISR', 'billingMode', 'notificationGroupingMode', 'NIT', 'accountNumber', 'creationTime', 'lastSignInTime', 'actions'];

    private readonly billingModeLabels: Record<string, string> = {
        DAILY  : 'Diario',
        WEEKLY : 'Semanal'
    };

    private readonly groupingModeLabels: Record<string, string> = {
        partner        : 'Cliente',
        partner_entity : 'Entidad',
        partner_route  : 'Ruta',
        partner_bus    : 'Bus'
    };
    canCreatePartner$   : Observable<boolean>;
    canEditPartner$     : Observable<boolean>;
    canDeletePartner$   : Observable<boolean>;
    canSendWelcome$     : Observable<boolean>;

    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild(MatPaginator) paginator!: MatPaginator;

    constructor(private adminService: AdminService, private dialog: MatDialog, private permissionService: PermissionService) {
        this.canCreatePartner$ = this.permissionService.hasPermission('partners.create');
        this.canEditPartner$   = this.permissionService.hasPermission('partners.update');
        this.canDeletePartner$ = of(false);
        this.canSendWelcome$   = this.permissionService.hasPermission('partners.update');

        this.dataSource.filterPredicate = (data: Partner, filter: string) => {
            const searchString       = filter.trim().toLowerCase();
            const nameMatch          = (data.displayName || '').toLowerCase().includes(searchString);
            const emailMatch         = data.email.toLowerCase().includes(searchString);
            const nitMatch           = data.NIT.toLowerCase().includes(searchString);
            const accountNumberMatch = (data.accountNumber || '').toLowerCase().includes(searchString);
            const billingModeMatch   = this.getBillingModeLabel(data.billingMode).toLowerCase().includes(searchString);
            const groupingModeMatch  = this.getGroupingModeLabel(data.notificationGroupingMode).toLowerCase().includes(searchString);

            return nameMatch || emailMatch || nitMatch || accountNumberMatch || billingModeMatch || groupingModeMatch;
        };
    }

    ngOnInit(): void {
        this.loadPartners();
	}

    ngAfterViewInit(): void {
        this.dataSource.sort      = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    async loadPartners(): Promise<void> {
        const result = await this.adminService.getAllPartners();

        if(result) {
            this.dataSource.data = result;
        }
    }

    getBillingModeLabel(mode?: string): string {
        return this.billingModeLabels[mode || ''] || 'N/A';
    }

    getGroupingModeLabel(mode?: string): string {
        return this.groupingModeLabels[mode || ''] || 'N/A';
    }

    applyFilter(event: Event): void {
		const filterValue = (event.target as HTMLInputElement).value;
		this.dataSource.filter = filterValue.trim().toLowerCase();

		if(this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
	}

    async onCreatePartner(): Promise<void> {
        const dialogRef = this.dialog.open(PartnerForm, {
			width        : '600px',
			disableClose : true,
			data         : null
        });
                
		dialogRef.afterClosed().subscribe(async (result: PartnerFormData | undefined) => {
            if(result) {
                const success = await this.adminService.createPartner({
                    email         : result.email,
                    displayName   : result.displayName,
                    disabled      : result.disabled,
                    paysISR       : result.paysISR,
                    billingMode   : result.billingMode,
                    notificationGroupingMode: result.notificationGroupingMode,
                    NIT           : result.NIT,
					accountNumber : result.accountNumber,
                    sendEmail     : result.sendEmail
                });

                if(success) {
                    this.loadPartners();
                }
            }
        });
    }

    async onEditPartner(partner: Partner): Promise<void> {
        const formData: PartnerFormData = {
            uid           : partner.uid,
            email         : partner.email,
            displayName   : partner.displayName,
            disabled      : partner.disabled,
            paysISR       : partner.paysISR,
            billingMode   : partner.billingMode,
            notificationGroupingMode: partner.notificationGroupingMode,
            NIT           : partner.NIT,
			accountNumber : partner.accountNumber!
        };

        const dialogRef = this.dialog.open(PartnerForm, {
			width: '600px',
			disableClose: true,
			data: formData
        });

        dialogRef.afterClosed().subscribe(async (result: PartnerFormData | undefined) => {
            if(result && result.uid) {
                const partnerUid = result.uid;

                const updatePayload: Partial<Omit<PartnerFormData, 'uid'>> = {
                    email         : result.email,
                    displayName   : result.displayName,
                    disabled      : result.disabled,
                    paysISR       : result.paysISR,
                    billingMode   : result.billingMode,
                    notificationGroupingMode: result.notificationGroupingMode,
                	NIT           : result.NIT,
					accountNumber : result.accountNumber
                };

                const success = await this.adminService.updatePartner(partnerUid, updatePayload);

                if(success) {
                    this.loadPartners();
                }
            }
        });
    }

    async onDeletePartner(uid: string): Promise<void> {
        if(window.confirm('¿Estás seguro de que quieres eliminar este cliente? Esta acción es irreversible.')) {
            const success = await this.adminService.deletePartner(uid);

            if(success) {
                this.loadPartners();
            }
    	}
    }

    async onSendBulkWelcomeEmails(): Promise<void> {
        if(!window.confirm('¿Confirmas el envío masivo de correos de bienvenida? Esto restablecerá las contraseñas.')) {
            return;
        }

        const success = await this.adminService.sendBulkWelcomeEmails();

        if(success) {
            this.loadPartners();
        }
    }
}
